import React, { useState } from "react";
import { Flag, X, Loader2, Check } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  "Inappropriate or offensive content",
  "Harassment or bullying",
  "Spam or scam",
  "Impersonation",
  "Something else",
];

/**
 * Safety Shield — a small "Report" flag visible on any user-generated artifact (card message,
 * voice/video keepsake, Maison Reply). Works for logged-out recipients too (public
 * /api/report-content endpoint), since most people view a digital card without an account.
 */
const ReportContentButton = ({ contentType, contentId, label = "Report" }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!reason) return;
    setSending(true);
    try {
      const token = localStorage.getItem("gifted_token");
      await axios.post(`${API}/report-content`, { content_type: contentType, content_id: contentId, reason, details },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      setSent(true);
    } catch (e) {
      console.error("Report content failed:", e);
    } finally { setSending(false); }
  };

  return (
    <>
      <button type="button" data-testid={`report-btn-${contentType}`} onClick={() => setOpen(true)}
        className="text-[11px] text-muted-foreground/60 hover:text-red-400 flex items-center gap-1 transition-colors">
        <Flag className="w-3 h-3" /> {label}
      </button>
      {open && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-[hsl(38_30%_12%_/_0.5)] backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="report-content-modal">
          <div className="surface rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            {sent ? (
              <div className="text-center py-4" data-testid="report-content-sent">
                <Check className="w-8 h-8 text-secondary mx-auto mb-2" />
                <p className="text-sm">Thank you — our Trust &amp; Safety team will review this shortly.</p>
                <button onClick={() => { setOpen(false); setSent(false); setReason(""); setDetails(""); }} className="text-xs text-primary mt-3">Close</button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-serif text-lg flex items-center gap-1.5"><Flag className="w-4 h-4 text-red-400" /> Report this content</h3>
                  <button onClick={() => setOpen(false)} data-testid="report-content-close"><X className="w-4 h-4 text-muted-foreground" /></button>
                </div>
                <div className="space-y-1.5 mb-3" data-testid="report-reason-options">
                  {REASONS.map((r) => (
                    <button key={r} type="button" onClick={() => setReason(r)}
                      className={`w-full text-left text-xs rounded-lg px-3 py-2 border ${reason === r ? "bg-red-500/10 text-red-500 border-red-500/30" : "surface text-muted-foreground"}`}>
                      {r}
                    </button>
                  ))}
                </div>
                <textarea data-testid="report-details" value={details} onChange={(e) => setDetails(e.target.value)} rows={2}
                  placeholder="Additional details (optional)" className="w-full bg-input border border-border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring/40 resize-none mb-3" />
                <button data-testid="report-submit-btn" onClick={submit} disabled={!reason || sending}
                  className="w-full text-sm bg-red-600 text-white rounded-lg py-2.5 hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Report"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ReportContentButton;
